import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { api } from '@/lib/api';

export interface User {
  id: string;
  email: string;
  role: string;
}

// Key to remember if user has logged in before (not the actual session)
const SESSION_KEY = 'tirker_has_session';

export const useAuthStore = defineStore('auth', () => {
  const user = ref<User | null>(null);
  const loading = ref(false);
  const error = ref<string | null>(null);
  const initialized = ref(false);

  // Check if we might have a session (used to avoid unnecessary API calls)
  const hasSessionFlag = ref(localStorage.getItem(SESSION_KEY) === 'true');

  const isAuthenticated = computed(() => !!user.value);
  const isAdmin = computed(() => user.value?.role === 'admin');

  async function login(email: string, password: string) {
    loading.value = true;
    error.value = null;

    try {
      const response = await api.post<{ user: User }>('/auth/login', { email, password });
      user.value = response.user;
      // Remember that we have a session
      localStorage.setItem(SESSION_KEY, 'true');
      hasSessionFlag.value = true;
      return true;
    } catch (e: any) {
      error.value = e.message || 'Login failed';
      return false;
    } finally {
      loading.value = false;
    }
  }

  async function logout() {
    try {
      await api.post('/auth/logout');
    } catch {
      // Ignore logout errors
    }
    user.value = null;
    localStorage.removeItem(SESSION_KEY);
    hasSessionFlag.value = false;
  }

  async function fetchUser() {
    // Always try to fetch - the HttpOnly cookie will be sent automatically
    loading.value = true;
    try {
      const response = await api.get<{ user: User }>('/auth/me');
      user.value = response.user;
      // Update session flag on success
      localStorage.setItem(SESSION_KEY, 'true');
      hasSessionFlag.value = true;
    } catch {
      // Session invalid or expired, clear flag
      user.value = null;
      localStorage.removeItem(SESSION_KEY);
      hasSessionFlag.value = false;
    } finally {
      loading.value = false;
      initialized.value = true;
    }
  }

  // Initialize auth state - call this on app startup
  async function initialize() {
    if (initialized.value) return;

    // Only try to fetch user if we think we have a session
    // This avoids unnecessary API calls for users who never logged in
    if (hasSessionFlag.value) {
      await fetchUser();
    } else {
      initialized.value = true;
    }
  }

  return {
    user,
    loading,
    error,
    initialized,
    hasSessionFlag,
    isAuthenticated,
    isAdmin,
    login,
    logout,
    fetchUser,
    initialize,
  };
});
